import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { getDatabase, ref, get } from "firebase/database";

/*colunas do excel*/
export function getExcelCol(index) {
    const A = 65;
    let col = '';
    while (index >= 0) {
        col = String.fromCharCode((index % 26) + A) + col;
        index = Math.floor(index / 26) - 1;
    }
    return col;
}

/*ordenação de colaboradores*/
export function sortEmployees(employees) {
    return employees.sort((a, b) => {
        return a.employee.localeCompare(b.employee);
    });
}

/* Função para verificar funcionários removidos */
async function checkRemovedEmployees(company, department, selectedMonth, selectedYear) {
    const db = getDatabase();
    const removedEmployees = {};

    try {
        const deptRef = ref(db, `companies/${company}/${department}`);
        const snapshot = await get(deptRef);

        if (snapshot.exists()) {
            const deptData = snapshot.val();

            for (const [employeeKey, employeeData] of Object.entries(deptData)) {
                if (['isDeptVisible', 'instrutores', 'ordem'].includes(employeeKey)) continue;
                if (typeof employeeData !== 'object') continue;

                if (employeeData['deleted-at']) {
                    const deletedDate = new Date(employeeData['deleted-at']);
                    const deletedMonth = deletedDate.getMonth() + 1;
                    const deletedYear = deletedDate.getFullYear();

                    if (parseInt(selectedMonth) === deletedMonth &&
                        parseInt(selectedYear) === deletedYear) {

                        const formattedDate = `${String(deletedDate.getDate()).padStart(2, '0')}-${String(deletedMonth).padStart(2, '0')}-${deletedYear}`;

                        if (!removedEmployees[formattedDate]) {
                            removedEmployees[formattedDate] = [];
                        }

                        const employeeName = employeeData.NAME || employeeKey;
                        removedEmployees[formattedDate].push(employeeName);
                    }
                }
            }
        }
    } catch (error) {
        console.error('Erro ao verificar funcionários removidos:', error);
    }

    return removedEmployees;
}

/* Função para verificar funcionários adicionados */
async function checkAddedEmployees(company, department, selectedMonth, selectedYear) {
    const db = getDatabase();
    const addedEmployees = {};

    try {
        const deptRef = ref(db, `companies/${company}/${department}`);
        const snapshot = await get(deptRef);

        if (snapshot.exists()) {
            const deptData = snapshot.val();

            for (const [employeeKey, employeeData] of Object.entries(deptData)) {
                if (['isDeptVisible', 'instrutores', 'ordem'].includes(employeeKey)) continue;
                if (typeof employeeData !== 'object') continue;

                if (employeeData['added-at']) {
                    const addedDate = new Date(employeeData['added-at']);
                    const addedMonth = addedDate.getMonth() + 1;
                    const addedYear = addedDate.getFullYear();

                    if (parseInt(selectedMonth) === addedMonth &&
                        parseInt(selectedYear) === addedYear) {

                        const formattedDate = `${String(addedDate.getDate()).padStart(2, '0')}-${String(addedMonth).padStart(2, '0')}-${addedYear}`;

                        if (!addedEmployees[formattedDate]) {
                            addedEmployees[formattedDate] = [];
                        }

                        const employeeName = employeeData.NAME || employeeKey;
                        addedEmployees[formattedDate].push(employeeName);
                    }
                }
            }
        }
    } catch (error) {
        console.error('Erro ao verificar funcionários adicionados:', error);
    }

    return addedEmployees;
}

/*nova aba da planilha para um departamento de uma empresa*/
export async function addDepartmentSheet(
    workbook,
    company,
    department,
    sessionDates,
    instructorList,
    allEmployees,
    selectedMonth,
    selectedYear,
    selectedCompany
) {
    const isAggregated = allEmployees.length === 1 && allEmployees[0].employee === '__AGGREGATED__';
    const sessionComments = isAggregated
        ? allEmployees[0].sessionComments || {}
        : allEmployees[0]?.sessionComments || {};

    const removedEmployees = await checkRemovedEmployees(selectedCompany, department, selectedMonth, selectedYear);
    const addedEmployees = await checkAddedEmployees(selectedCompany, department, selectedMonth, selectedYear);

    const sanitizeWorksheetName = (name) => {
        return name.replace(/[*?:\\/[\]]/g, ' ').substring(0, 31);
    };

    const worksheet = workbook.addWorksheet(sanitizeWorksheetName(department));

    const blackBorder = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } }
    };

    const lastDataColumn = sessionDates.length + 4;

    const titleRow = worksheet.addRow(['CONTROLE DE ADESÃO AO PROGRAMA DE GINÁSTICA LABORAL']);
    titleRow.height = 25;
    titleRow.eachCell(cell => {
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF1f4e79' }
        };
        cell.font = {
            bold: true,
            color: { argb: 'FFFFFFFF' },
            name: 'Arial',
            size: 10
        };
        cell.alignment = {
            vertical: 'middle',
            horizontal: 'center'
        };
        cell.border = blackBorder;
    });
    worksheet.mergeCells(`A${titleRow.number}:${getExcelCol(lastDataColumn)}${titleRow.number}`);

    const monthRow = worksheet.addRow([`Mês: ${selectedMonth} | Prof. Responsável: ${instructorList}`]);
    monthRow.eachCell(cell => {
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF1f4e79' }
        };
        cell.font = {
            bold: true,
            color: { argb: 'FFFFFFFF' },
            name: 'Arial',
            size: 10
        };
        cell.alignment = {
            vertical: 'middle',
            horizontal: 'center'
        };
        cell.border = blackBorder;
    });
    worksheet.mergeCells(`A${monthRow.number}:${getExcelCol(lastDataColumn)}${monthRow.number}`);

    const companyDeptRow = worksheet.addRow([`Empresa: ${company} | Departamento: ${department}`]);
    companyDeptRow.eachCell(cell => {
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF1f4e79' }
        };
        cell.font = {
            bold: true,
            color: { argb: 'FFFFFFFF' },
            name: 'Arial',
            size: 10
        };
        cell.alignment = {
            vertical: 'middle',
            horizontal: 'center'
        };
        cell.border = blackBorder;
    });
    worksheet.mergeCells(`A${companyDeptRow.number}:${getExcelCol(lastDataColumn)}${companyDeptRow.number}`);

    const countableSessions = sessionDates.filter(date => {
        const firstEmployee = allEmployees[0];
        return firstEmployee.sessionAttendanceFlags?.[date] !== false;
    });
    const countableSessionCount = countableSessions.length;

    if (isAggregated) {
        const formattedDates = sessionDates.map(date => date.split('-')[0]);
        const headerLabels = ['DIAS', ...formattedDates, 'Freq. Mensal'];
        const headerRow = worksheet.addRow(headerLabels);

        headerRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF1f4e79' }
            };
            cell.font = {
                bold: true,
                color: { argb: 'FFFFFFFF' },
                name: 'Arial',
                size: 10
            };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.border = blackBorder;

            if (colNumber > 1 && colNumber <= sessionDates.length + 1) {
                const dateIndex = colNumber - 2;
                const dateKey = sessionDates[dateIndex];
                if (sessionComments[dateKey]) {
                    cell.note = {
                        texts: [{ text: sessionComments[dateKey] }],
                        properties: {
                            width: 200,
                            height: 100,
                            margins: { left: 0.1, right: 0.1, top: 0.1, bottom: 0.1 }
                        }
                    };
                }
            }
        });

        const aggregateData = allEmployees[0].presences;
        const rowValues = ['TOTAL PRESENÇA'];
        sessionDates.forEach(date => {
            const data = aggregateData[date];
            const shouldCount = allEmployees[0].sessionAttendanceFlags?.[date] !== false;
            rowValues.push(shouldCount ? (data ? parseInt(data.present) || 0 : 0) : '');
        });

        const totalPresent = rowValues.slice(1).reduce((sum, val) => sum + (parseInt(val) || 0), 0);
        const dataRow = worksheet.addRow([...rowValues, totalPresent]);

        dataRow.eachCell(cell => {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFFFFFFF' }
            };
            cell.font = {
                name: 'Arial',
                size: 10,
                bold: true
            };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.border = blackBorder;
        });

        const colsConfig = [
            { col: 1, width: 20 },
            ...sessionDates.map((_, i) => ({
                col: i + 2,
                width: 5,
                numFmt: '0'
            })),
            {
                col: sessionDates.length + 2,
                width: 15,
                numFmt: '0'
            }
        ];

        colsConfig.forEach(config => {
            const col = worksheet.getColumn(config.col);
            col.width = config.width;
            if (config.numFmt) col.numFmt = config.numFmt;
        });

        const lastUsedCol = sessionDates.length + 2;
        for (let i = lastUsedCol + 1; i <= worksheet.columnCount; i++) {
            const col = worksheet.getColumn(i);
            col.width = 0;
            col.hidden = true;
        }
    } else {
        const formattedDates = sessionDates.map(date => date.split('-')[0]);
        const headerRow = worksheet.addRow(['#', 'Nome do Funcionário', ...formattedDates, 'Freq. Mensal', '% Colaborador']);

        headerRow.eachCell((cell, colNumber) => {
            const isDateHeader = colNumber >= 3 && colNumber <= sessionDates.length + 2;
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: isDateHeader ? 'FFDEEBF7' : 'FF1f4e79' }
            };
            cell.font = {
                bold: true,
                color: { argb: isDateHeader ? 'FF000000' : 'FFFFFFFF' },
                name: 'Arial',
                size: 10
            };
            cell.alignment = {
                vertical: 'middle',
                horizontal: 'center'
            };
            cell.border = blackBorder;

            if (isDateHeader && sessionComments[sessionDates[colNumber - 3]]) {
                cell.note = {
                    texts: [{ text: sessionComments[sessionDates[colNumber - 3]] }],
                    properties: {
                        width: 200,
                        height: 100,
                        margins: { left: 0.1, right: 0.1, top: 0.1, bottom: 0.1 }
                    }
                };
            }
        });

        const dataStartRow = headerRow.number + 1;
        allEmployees = sortEmployees(allEmployees);

        for (const emp of allEmployees) {
            const currentRowNumber = dataStartRow + allEmployees.indexOf(emp);
            const row = [currentRowNumber - dataStartRow + 1, emp.employee];

            let leaveStartIndex = -1;
            let leaveEndIndex = -1;

            if (emp.daysOffBeginning && emp.daysOffEnding) {
                const [startDay, startMonth, startYear] = emp.daysOffBeginning.split('/');
                const [endDay, endMonth, endYear] = emp.daysOffEnding.split('/');

                const startDate = new Date(`${startYear}-${startMonth.padStart(2, '0')}-${startDay.padStart(2, '0')}`);
                const endDate = new Date(`${endYear}-${endMonth.padStart(2, '0')}-${endDay.padStart(2, '0')}`);

                sessionDates.forEach((date, index) => {
                    const [day, month, year] = date.split('-');
                    const sessionDate = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);

                    if (sessionDate >= startDate && sessionDate <= endDate) {
                        if (leaveStartIndex === -1) leaveStartIndex = index;
                        leaveEndIndex = index;
                    }
                });
            }

            sessionDates.forEach((date, index) => {
                const status = emp.presences[date];
                const shouldCount = emp.sessionAttendanceFlags?.[date] !== false;

                if (leaveStartIndex !== -1 && index >= leaveStartIndex && index <= leaveEndIndex) {
                    if (index === leaveStartIndex) {
                        const licenseText = emp.additionalInfo === 'LICENÇA MATERNIDADE' ? 'LM'
                            : emp.additionalInfo === 'FÉRIAS' ? 'FE'
                                : emp.additionalInfo === 'AFASTADO' ? 'AF'
                                    : emp.additionalInfo || '-';
                        row.push(licenseText);
                    } else {
                        row.push('');
                    }
                } else if (shouldCount) {
                    row.push(status === 'PRESENTE' ? 1 : '');
                } else {
                    row.push('');
                }
            });

            const firstDateCol = 'C';
            const lastDateCol = getExcelCol(sessionDates.length + 1);

            row.push({
                formula: `=COUNTIF(${firstDateCol}${currentRowNumber}:${lastDateCol}${currentRowNumber},1)`
            });

            row.push({
                formula: `=IF(${getExcelCol(sessionDates.length + 2)}${currentRowNumber}>0, ${getExcelCol(sessionDates.length + 2)}${currentRowNumber}/${countableSessionCount}, 0)`
            });

            const addedRow = worksheet.addRow(row);
            addedRow.eachCell((cell, colNumber) => {
                cell.font = { name: 'Arial', size: 10 };
                cell.border = blackBorder;

                if (colNumber === 2) {
                    cell.alignment = { vertical: 'middle', horizontal: 'left' };
                    if (emp.statusNote) {
                        cell.note = {
                            texts: [{ text: emp.statusNote }],
                            properties: {
                                width: 200,
                                height: 100,
                                margins: { left: 0.1, right: 0.1, top: 0.1, bottom: 0.1 }
                            }
                        };
                    }
                } else {
                    cell.alignment = { vertical: 'middle', horizontal: 'center' };
                }

                if (colNumber >= 3 && colNumber <= sessionDates.length + 2) {
                    const dateIndex = colNumber - 3;
                    const dateKey = sessionDates[dateIndex];

                    const wasRemoved = removedEmployees[dateKey] && removedEmployees[dateKey].includes(emp.employee);
                    const wasAdded = addedEmployees[dateKey] && addedEmployees[dateKey].includes(emp.employee);

                    if (wasRemoved && cell.value === 1) {
                        cell.note = { texts: [{ text: 'REMOVIDO' }], properties: { width: 100, height: 50 } };
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1f4e79' } };
                        cell.font = { color: { argb: 'FF1f4e79' } };
                    } else if (wasAdded && cell.value === 1) {
                        cell.note = { texts: [{ text: 'ADICIONADO' }], properties: { width: 100, height: 50 } };
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1f4e79' } };
                        cell.font = { color: { argb: 'FF1f4e79' } };
                    } else if (wasRemoved) {
                        cell.note = { texts: [{ text: 'REMOVIDO' }], properties: { width: 100, height: 50 } };
                    } else if (wasAdded) {
                        cell.note = { texts: [{ text: 'ADICIONADO' }], properties: { width: 100, height: 50 } };
                    } else if (cell.value === 1) {
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1f4e79' } };
                        cell.font = { color: { argb: 'FF1f4e79' } };
                    }

                    const shouldCount = emp.sessionAttendanceFlags?.[dateKey] !== false;
                    if (!shouldCount) {
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
                        cell.note = { texts: [{ text: 'Sessão não contabilizada' }], properties: { width: 150, height: 50 } };
                    }
                }
            });
        }

        const totalRowNumber = worksheet.lastRow.number + 1;
        const lastDataRow = totalRowNumber - 1;
        const totalRow = ['', 'TOTAL PRESENÇA'];
        sessionDates.forEach((_, index) => {
            const colLetter = getExcelCol(index + 2);
            totalRow.push({ formula: `=COUNTIF(${colLetter}${dataStartRow}:${colLetter}${lastDataRow},1)` });
        });

        totalRow.push({
            formula: `=SUM(${getExcelCol(sessionDates.length + 2)}${dataStartRow}:${getExcelCol(sessionDates.length + 2)}${lastDataRow})`
        });
        totalRow.push({
            formula: `=MIN(COUNTIF(${getExcelCol(sessionDates.length + 3)}${dataStartRow}:${getExcelCol(sessionDates.length + 3)}${lastDataRow}, ">0") / (COUNTA(${getExcelCol(sessionDates.length + 3)}${dataStartRow}:${getExcelCol(sessionDates.length + 3)}${lastDataRow}) * 0.95), 1)`
        });

        const totalLine = worksheet.addRow(totalRow);
        totalLine.eachCell(cell => {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF1f4e79' }
            };
            cell.font = {
                bold: true,
                color: { argb: 'FFFFFFFF' },
                name: 'Arial',
                size: 10
            };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.border = blackBorder;
        });

        worksheet.getColumn(1).width = 5;
        worksheet.getColumn(2).width = 30;
        for (let i = 3; i <= sessionDates.length + 2; i++) {
            worksheet.getColumn(i).width = 5;
            worksheet.getColumn(i).numFmt = '0';
        }
        worksheet.getColumn(sessionDates.length + 3).width = 15;
        worksheet.getColumn(sessionDates.length + 4).width = 18;
        worksheet.getColumn(sessionDates.length + 4).numFmt = '0%';
    }

    worksheet.eachRow(row => {
        row.height = 15;
    });

    for (let i = lastDataColumn + 1; i <= worksheet.columnCount; i++) {
        worksheet.getColumn(i).width = 0;
        worksheet.getColumn(i).hidden = true;
    }

    worksheet.views = [{ showGridLines: false }];
}

/*calculos*/
export function calculateSummary(allEmployees, sessionDates) {
    const totalPresencasPorData = Array(sessionDates.length).fill(0);
    const totalPessoasPorData = Array(sessionDates.length).fill(0);
    const totalFuncionarios = allEmployees.length;

    const isAggregated = allEmployees.length === 1 && allEmployees[0].employee === '__AGGREGATED__';

    const countableSessions = sessionDates.filter(date => {
        const firstEmployee = allEmployees[0];
        return firstEmployee.sessionAttendanceFlags?.[date] !== false;
    });
    const countableSessionCount = countableSessions.length;

    if (isAggregated) {
        const aggregateData = allEmployees[0].presences;

        sessionDates.forEach((date, idx) => {
            const data = aggregateData[date];
            const shouldCount = allEmployees[0].sessionAttendanceFlags?.[date] !== false;

            if (data && shouldCount) {
                totalPresencasPorData[idx] = parseInt(data.present) || 0;
                totalPessoasPorData[idx] = parseInt(data.total) || 0;
            } else if (data) {
                totalPresencasPorData[idx] = 0;
                totalPessoasPorData[idx] = 0;
            }
        });
    } else {
        for (const emp of allEmployees) {
            sessionDates.forEach((date, idx) => {
                const shouldCount = emp.sessionAttendanceFlags?.[date] !== false;

                if (shouldCount) {
                    const status = emp.presences?.[date];
                    if (status === 'PRESENTE') {
                        totalPresencasPorData[idx]++;
                    }
                    totalPessoasPorData[idx]++;
                } else {
                    totalPresencasPorData[idx] += 0;
                    totalPessoasPorData[idx] += 0;
                }
            });
        }
    }

    const totalPresencas = totalPresencasPorData.reduce((a, b) => a + b, 0);

    const totalPessoas = isAggregated
        ? totalPessoasPorData.reduce((a, b) => a + b, 0)
        : totalFuncionarios * countableSessionCount;

    const percentual = totalPessoas > 0 ? (totalPresencas / totalPessoas) : 0;

    return {
        dates: sessionDates,
        presencas: totalPresencasPorData,
        totalSetor: isAggregated ? totalPessoasPorData : Array(sessionDates.length).fill(totalFuncionarios),
        total: totalPresencas,
        percentual,
        isAggregated,
        totalPresent: totalPresencas,
        totalPossible: totalPessoas,
        totalEmployees: totalFuncionarios,
        countableSessionCount: countableSessionCount
    };
}

/*aba geral que fica no final de um arquivo*/
export function addGeralSheet(workbook, geralData, selectedMonth) {
    const geralSheet = workbook.addWorksheet('GERAL');

    geralSheet.eachRow(row => {
        row.eachCell(cell => {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFFFFFFF' }
            };
            cell.font = {
                name: 'Arial',
                size: 10
            };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
        });
    });

    const allDates = [...new Set(Object.values(geralData).flatMap(d => d.dates))].sort();
    const allDatesDaysOnly = allDates.map(date => date.split('-')[0]);
    const lastColumn = 1 + allDates.length + 2;

    const blackBorder = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } }
    };

    const titleRow = geralSheet.addRow(['CONTROLE DE ADESÃO AO PROGRAMA DE GINÁSTICA LABORAL']);
    titleRow.height = 25;
    titleRow.eachCell(cell => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1f4e79' } };
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, name: 'Arial', size: 10 };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border = blackBorder;
    });
    geralSheet.mergeCells(`A${titleRow.number}:${getExcelCol(lastColumn)}${titleRow.number}`);

    const mesRow = geralSheet.addRow([`MÊS: ${selectedMonth}`]);
    mesRow.eachCell(cell => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1f4e79' } };
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, name: 'Arial', size: 10 };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border = blackBorder;
    });
    geralSheet.mergeCells(`A${mesRow.number}:${getExcelCol(lastColumn)}${mesRow.number}`);

    const controleRow = geralSheet.addRow(['CONTROLE NUMERAL GERAL']);
    controleRow.eachCell(cell => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1f4e79' } };
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, name: 'Arial', size: 10 };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border = blackBorder;
    });
    geralSheet.mergeCells(`A${controleRow.number}:${getExcelCol(lastColumn)}${controleRow.number}`);

    // --- Cabeçalho ---
    const header = ['Departamento', ...allDatesDaysOnly, '% Presença', 'Total Presente'];
    const headerRow = geralSheet.addRow(header);
    headerRow.eachCell({ includeEmpty: true }, (cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1f4e79' } };
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, name: 'Arial', size: 10 };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border = blackBorder;
    });

    const deptSheetNameMap = {};
    workbook.worksheets.forEach(ws => {
        const normalizedName = ws.name.replace(/\s+|\/+/g, '').toLowerCase();
        deptSheetNameMap[normalizedName] = ws.name;
    });

    const dataStartRow = headerRow.number + 1;

    for (const [dept, data] of Object.entries(geralData)) {
        const row = [dept];

        allDates.forEach(date => {
            const idx = data.dates.indexOf(date);
            const value = idx !== -1 ? parseInt(data.presencas[idx]) || 0 : 0;
            row.push(value);
        });

        const normalizedDept = dept.replace(/\s+|\/+/g, '').toLowerCase();
        const realSheetName = deptSheetNameMap[normalizedDept];

        if (realSheetName) {
            const deptSheet = workbook.getWorksheet(realSheetName);
            const lastRowDept = deptSheet.lastRow.number;
            const penultimateColDept = deptSheet.columnCount - 2;
            const cellRef = `${getExcelCol(penultimateColDept)}${lastRowDept}`;
            row.push({ formula: `='${realSheetName}'!${cellRef}` });
        } else {
            row.push(0);
        }

        row.push(data.totalPresent || 0);

        const dataRow = geralSheet.addRow(row);
        dataRow.eachCell((cell, col) => {
            cell.font = { name: 'Arial', size: 10 };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.border = blackBorder;

            if (col === allDates.length + 2) cell.numFmt = '0.00%';
            else if (col > allDates.length + 1) { cell.numFmt = '0'; cell.font.bold = true; }
        });
    }

    // --- Total Geral ---
    const firstDataRow = dataStartRow;
    const lastDataRow = geralSheet.lastRow.number;
    const totalRow = ['TOTAL GERAL'];

    for (let i = 0; i < allDates.length; i++) {
        const col = getExcelCol(1 + i);
        totalRow.push({ formula: `=SUM(${col}${firstDataRow}:${col}${lastDataRow})` });
    }

    totalRow.push({ formula: `=AVERAGE(${getExcelCol(allDates.length + 1)}${firstDataRow}:${getExcelCol(allDates.length + 1)}${lastDataRow})` });
    totalRow.push({ formula: `=SUM(${getExcelCol(allDates.length + 2)}${firstDataRow}:${getExcelCol(allDates.length + 2)}${lastDataRow})` });

    const totalLine = geralSheet.addRow(totalRow);
    totalLine.eachCell((cell, col) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1f4e79' } };
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, name: 'Arial', size: 10 };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border = blackBorder;

        if (col === allDates.length + 2) cell.numFmt = '0.00%';
        else if (col > allDates.length + 1) cell.numFmt = '0';
    });

    geralSheet.getColumn(1).width = 25;
    for (let i = 2; i <= allDates.length + 1; i++) { geralSheet.getColumn(i).width = 8; geralSheet.getColumn(i).numFmt = '0'; }
    geralSheet.getColumn(allDates.length + 2).width = 12; geralSheet.getColumn(allDates.length + 2).numFmt = '0%';
    geralSheet.getColumn(allDates.length + 3).width = 15; geralSheet.getColumn(allDates.length + 3).numFmt = '0';
    for (let i = lastColumn + 1; i <= geralSheet.columnCount; i++) { geralSheet.getColumn(i).width = 0; geralSheet.getColumn(i).hidden = true; }

    geralSheet.views = [{ showGridLines: false }];
}


/*informações do colaborador*/
export function buildEmployeeData(sessions, sessionDates, department, getRollData, rawData) {
    const employeesMap = {};
    const aggregateData = {};
    const sessionComments = {};
    const sessionsByDate = {};
    const sessionAttendanceFlags = {};

    const normalizeName = (name) => {
        return name.trim().replace(/\s+/g, ' ').toLowerCase();
    };

    sessions.forEach(session => {
        const sessionData = getRollData(
            rawData,
            session.company,
            department,
            session.keyDate,
            session.year,
            session.month
        );

        let dateKey = '';
        if (sessionData && sessionData.originalTime) {
            const [day, month, year] = sessionData.originalTime.split('-').slice(0, 3);
            dateKey = `${day}-${month}-${year}`;
        } else {
            dateKey = session.keyDate.split('-').slice(0, 3).join('-');
        }

        sessionAttendanceFlags[dateKey] = sessionData?.countAttendance !== false;

        if (!sessionsByDate[dateKey]) {
            sessionsByDate[dateKey] = [];
        }
        sessionsByDate[dateKey].push(sessionData);

        if (sessionData) {
            if (sessionData.comment && !sessionComments[dateKey]) {
                sessionComments[dateKey] = sessionData.comment;
            }

            if (sessionData.presentPeople !== undefined || sessionData.totalofPeople !== undefined) {
                if (!aggregateData[dateKey]) {
                    aggregateData[dateKey] = {
                        present: 0,
                        total: 0,
                        comment: sessionData.comment || '',
                        countAttendance: sessionData.countAttendance !== false
                    };
                }
                const present = parseInt(sessionData.presentPeople !== undefined ? sessionData.presentPeople : 0) || 0;
                const total = parseInt(sessionData.totalofPeople !== undefined ? sessionData.totalofPeople : present) || 0;

                aggregateData[dateKey].present += present;
                aggregateData[dateKey].total += total;
            } else {
                if (!aggregateData[dateKey]) {
                    aggregateData[dateKey] = {
                        present: 0,
                        total: 0,
                        comment: sessionData.comment || '',
                        countAttendance: sessionData.countAttendance !== false
                    };
                }
            }

            if (sessionData.employees) {
                let employeesArray = Array.isArray(sessionData.employees)
                    ? sessionData.employees
                    : Object.values(sessionData.employees || {});

                employeesArray.forEach(emp => {
                    if (!emp || typeof emp !== 'object') return;

                    const normalizedName = normalizeName(emp.employee);
                    const originalName = emp.employee.trim().replace(/\s+/g, ' ');

                    if (!employeesMap[normalizedName]) {
                        employeesMap[normalizedName] = {
                            employee: originalName,
                            presences: {},
                            sessionDetails: {},
                            firstAppearance: dateKey,
                            lastAppearance: dateKey,
                            statusNotes: {},
                            daysOffBeginning: emp.daysOffBeginning,
                            daysOffEnding: emp.daysOffEnding,
                            additionalInfo: emp.additionalInfo,
                            statusNote: '',
                            sessionAttendanceFlags: {}
                        };
                    }

                    const originalTimeKey = sessionData.originalTime;

                    employeesMap[normalizedName].sessionDetails[originalTimeKey] = emp.status;
                    employeesMap[normalizedName].sessionAttendanceFlags[dateKey] = sessionData.countAttendance !== false;

                    if (emp.status === 'PRESENTE' || !employeesMap[normalizedName].presences[dateKey]) {
                        employeesMap[normalizedName].presences[dateKey] = emp.status;
                    }

                    if (emp.daysOffBeginning) {
                        employeesMap[normalizedName].daysOffBeginning = emp.daysOffBeginning;
                    }
                    if (emp.daysOffEnding) {
                        employeesMap[normalizedName].daysOffEnding = emp.daysOffEnding;
                    }
                    if (emp.additionalInfo) {
                        employeesMap[normalizedName].additionalInfo = emp.additionalInfo;
                    }
                });
            }
        }
    });

    if (Object.keys(employeesMap).length === 0 && Object.keys(aggregateData).length > 0) {
        return [{
            employee: '__AGGREGATED__',
            aggregated: true,
            presences: aggregateData,
            sessionComments: sessionComments,
            sessionAttendanceFlags: sessionAttendanceFlags
        }];
    }

    return Object.values(employeesMap).map(emp => ({
        ...emp,
        sessionComments: sessionComments,
        statusNotes: emp.statusNotes || {},
        sessionAttendanceFlags: sessionAttendanceFlags
    }));
}

/*exporta a planilha*/
export async function exportContentExcel(
    selectedCompany,
    selectedDepartment,
    selectedMonth,
    selectedYear,
    allCompanies,
    roll,
    getRollData,
    rawData,
    firebaseToString
) {
    if (!selectedCompany || !selectedMonth || !selectedYear) {
        const missingParams = [];
        if (!selectedCompany) missingParams.push('Empresa');
        if (!selectedMonth) missingParams.push('Mês');
        if (!selectedYear) missingParams.push('Ano');

        alert(`Preencha os campos: ${missingParams.join(', ')}`);
        return;
    }

    const monthNum = parseInt(selectedMonth);
    const yearNum = parseInt(selectedYear);
    if (isNaN(monthNum)) {
        alert('Mês selecionado é inválido.');
        return;
    }
    if (isNaN(yearNum)) {
        alert('Ano selecionado é inválido.');
        return;
    }

    try {
        if (!rawData || typeof rawData !== 'object' || Object.keys(rawData).length === 0) {
            alert('Dados não carregados corretamente. Tente recarregar a página.');
            return;
        }

        const monthStr = String(selectedMonth).padStart(2, '0');
        const targetMonthYear = `${monthStr}-${selectedYear}`;

        const departmentList = selectedDepartment
            ? [selectedDepartment]
            : Object.keys(allCompanies[selectedCompany] || {}).filter(
                dep => !['isCompanyVisible', 'instrutores', 'ordem'].includes(dep)
            );

        if (departmentList.length === 0) {
            alert('Nenhum departamento disponível para exportação.');
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const geralData = {};

        let orderedDepartments = departmentList;
        if (selectedCompany === "DELL") {
            const ordem = allCompanies[selectedCompany]?.ordem;
            if (ordem) {
                const ordemArray = ordem.split(",").map(dep => dep.trim());
                orderedDepartments = ordemArray.filter(dep => departmentList.includes(dep));
                const restantes = departmentList.filter(dep => !ordemArray.includes(dep));
                orderedDepartments = [...orderedDepartments, ...restantes];
            }
        }

        for (const department of orderedDepartments) {
            const allSessions = roll.filter(session => {
                if (session.company !== selectedCompany || session.department !== department) return false;

                let sessionData = null;

                try {
                    sessionData = getRollData(rawData, session.company, department, session.keyDate, session.year, session.month);
                } catch (e) { }

                if (!sessionData && rawData[session.keyDate]) {
                    sessionData = rawData[session.keyDate];
                }

                if (sessionData && sessionData.originalTime) {
                    const parts = sessionData.originalTime.split('-');
                    if (parts.length >= 3) {
                        const monthPart = parts[1].padStart(2, '0');
                        const yearPart = parts[2];
                        const sessionMonthYear = `${monthPart}-${yearPart}`;
                        return sessionMonthYear === targetMonthYear;
                    }
                }

                if (session.keyDate && session.keyDate.includes(targetMonthYear)) {
                    return true;
                }

                return false;
            });

            if (allSessions.length === 0) {
                console.warn(`Nenhuma sessão encontrada para ${department} em ${targetMonthYear}`);
                continue;
            }

            const sessionDates = [...new Set(allSessions.map(s => {
                let sessionData = null;
                try {
                    sessionData = getRollData(rawData, s.company, department, s.keyDate, s.year, s.month);
                } catch (e) { }

                if (!sessionData) {
                    const [day, month, year, time] = s.keyDate.split(/[-:]/);
                    const monthKey = month.replace(/^0/, '');
                    const yearKey = year;
                    sessionData = rawData?.[yearKey]?.[monthKey]?.[s.company]?.[department]?.[s.keyDate] || null;
                }

                if (!sessionData && rawData[s.keyDate]) {
                    sessionData = rawData[s.keyDate];
                }

                if (sessionData && sessionData.originalTime) {
                    const [day, month, year] = sessionData.originalTime.split('-').slice(0, 3);
                    return `${String(day).padStart(2, '0')}-${String(month).padStart(2, '0')}-${year}`;
                } else {
                    return s.keyDate.split('-').slice(0, 3).map(p => p.padStart(2, '0')).join('-');
                }
            }))].sort((a, b) => {
                const [da, ma, ya] = a.split('-').map(x => parseInt(x, 10));
                const [db, mb, yb] = b.split('-').map(x => parseInt(x, 10));
                return new Date(ya, ma - 1, da) - new Date(yb, mb - 1, db);
            });

            console.log("Datas finais selecionadas para planilha:", department, sessionDates);

            const allEmployees = buildEmployeeData(allSessions, sessionDates, department, getRollData, rawData);

            if (allEmployees?.length > 0) {
                await addDepartmentSheet(
                    workbook,
                    firebaseToString(selectedCompany),
                    firebaseToString(department),
                    sessionDates,
                    [...new Set(allSessions.map(s => s.instructor))].join(', '),
                    allEmployees,
                    selectedMonth,
                    selectedYear,
                    selectedCompany
                );
                geralData[firebaseToString(department)] = calculateSummary(allEmployees, sessionDates);
            }
        }

        if (Object.keys(geralData).length > 0) {
            addGeralSheet(workbook, geralData, `${String(selectedMonth).padStart(2, '0')}-${selectedYear}`);
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

            const filename = `${selectedCompany}${selectedDepartment ? `-${selectedDepartment}` : ''}-${String(selectedMonth).padStart(2, '0')}-${selectedYear}.xlsx`;
            saveAs(blob, filename);
        } else {
            alert('Nenhum dado encontrado para exportar.');
        }
    } catch (error) {
        alert(`Falha ao gerar planilha: ${error.message}`);
    }
}



